import './users-management.scss';
import { useCallback, useMemo, useState } from 'react';
import { ButtonAttributes } from '@celebration/design-system/dist/lib/button-group/props.types';
import { Tabs } from 'antd';
import type { TabsProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { BreadcrumbItem } from '@celebration/react/ClbBreadcrumb/ClbBreadcrumb.types';
import { ClbModal } from '@celebration/react';
import Subheader from '../../common/components/subheader';
import HomeConstants from '../home/constants';
import UsersTable from './components/users-table/users-table';
import UsersConstants from './constants/index';
import IActionButtonIcon from '../../common/components/table/interfaces/IActionButtonIcon';
import ToolbarTable from '../../common/components/toolbar-table';
import UserFormFullPageFlow from './components/user-form/user-form';
import Auth from '../../app/contexts/auth';
import { SULAHUB_ROLES } from '../../common/constants/roles';
import RequestsTable from './components/requests-table/requests-table';
import UserEditFormFullPageFlow from './components/user-edit-form/user-edit-form';
import { deactivateUser } from '../../common/services/sulahub-auth/sulahub-auth-service';
import { useToast } from '../../app/contexts/toast/toast.context';
import IUsersDataTable from './interfaces/IUsersDataTable';

function UsersManagementForm({ tab }: Readonly<{ tab: string }>) {
    const { isAuthorized } = Auth.useAuth();
    const { setToast } = useToast();

    const [showFilters, setShowFilters] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState<string>();
    const [activeTab, setActiveTab] = useState<string>(tab);
    const [isUserFormOpened, setIsUserFormOpened] = useState<boolean>(false);
    const [isUserEditFormOpened, setIsUserEditFormOpened] =
        useState<boolean>(false);
    const [getUserData, setGetUserData] = useState<boolean>(false);
    const { t } = useTranslation();

    const [editUserId, setEditUserId] = useState<string>();

    const [deactivateUserInfo, setDeactivateUserInfo] =
        useState<IUsersDataTable>();
    const [deactivateUserModalOpened, setDeactivateUserModalOpened] =
        useState(false);

    const toolbarSearchHandler = useCallback(
        async (term: string): Promise<void> => {
            setSearchTerm(term);
        },
        [setSearchTerm],
    );

    const toggleShowFilters = useCallback(() => {
        setShowFilters(!showFilters);
    }, [showFilters]);

    const breadcrumbList: BreadcrumbItem[] = [
        { label: HomeConstants.PAGE_TITLE, url: HomeConstants.PATH },
        { label: t(UsersConstants.PAGE_TITLE), url: UsersConstants.PATH },
    ];

    window.onbeforeunload = (e: any) => {
        if (isUserFormOpened) {
            e.preventDefault();
        }

        return e;
    };

    const CloseUserFormFullPageFlow = useCallback(
        (opened: boolean) => {
            setIsUserFormOpened(opened);
            setActiveTab('1');
            document.querySelector('body')?.style.removeProperty('overflow');
        },
        [setIsUserFormOpened],
    );

    const handleDeactivateUser = useCallback(() => {
        deactivateUser(deactivateUserInfo!.id).then(({ error }) => {
            if (error !== undefined) {
                setToast('negative', t('Error'), error.message);
                return;
            }

            setToast(
                'positive',
                t('DeactivateSuccess'),
                t('DeactivateSuccessMessage', {
                    userEmail: deactivateUserInfo!.email,
                }),
            );

            setGetUserData(true);
        });

        setDeactivateUserModalOpened(false);
    }, [deactivateUserInfo, setToast, t]);

    const renderUserFormFullPageFlow = () => {
        return (
            isUserFormOpened && (
                <UserFormFullPageFlow
                    isUserFormOpened={isUserFormOpened}
                    setIsUserFormOpened={CloseUserFormFullPageFlow}
                />
            )
        );
    };

    const renderUserFormEdit = () => {
        return (
            <UserEditFormFullPageFlow
                isUserEditFormOpened={isUserEditFormOpened}
                setIsUserEditFormOpened={setIsUserEditFormOpened}
                setEditUserId={setEditUserId}
                userId={editUserId ?? ''}
                setGetUsersData={setGetUserData}
            />
        );
    };

    const toolbarButtonList: ButtonAttributes[] = useMemo(() => {
        if (
            isAuthorized([
                SULAHUB_ROLES.SYSTEM_PROFILES.SULA_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.MESA_PLUS_ADMIN,
                SULAHUB_ROLES.SYSTEM_PROFILES.TP_ADMIN_USER,
            ])
        ) {
            return [
                {
                    id: 'new-user',
                    ariaLabel: t('NovoUsuario'),
                    label: t('NovoUsuario'),
                    title: t('NovoUsuario'),
                    callback: () => setIsUserFormOpened(true),
                },
            ];
        }

        return [];
    }, [isAuthorized, t]);

    const toolbarIconsActionsList: IActionButtonIcon[] = [
        {
            icon: 'Filter',
            tooltipLabel: t('Filtrar'),
            callback: () => toggleShowFilters(),
            disabled: activeTab !== '1',
        },
    ];

    const tabSelector = useCallback(
        (tab: string) => {
            setActiveTab(tab);
            setSearchTerm('');
        },
        [setActiveTab, setSearchTerm],
    );

    const renderUsersTab = () => {
        return (
            activeTab === '1' && (
                <UsersTable
                    searchTerm={searchTerm}
                    showFilters={showFilters}
                    toggleShowFilters={toggleShowFilters}
                    setIsUserEditFormOpened={setIsUserEditFormOpened}
                    setEditUserId={setEditUserId}
                    getUsersData={getUserData}
                    setGetUsersData={setGetUserData}
                    setDeactivateUserInfo={setDeactivateUserInfo}
                    setDeactivateUserModalOpened={setDeactivateUserModalOpened}
                />
            )
        );
    };

    const renderRequestsTab = () => {
        return (
            activeTab === '2' && (
                <RequestsTable
                    searchTerm={searchTerm}
                    showFilters={showFilters}
                    toggleShowFilters={toggleShowFilters}
                />
            )
        );
    };

    const renderUserDeactivateModal = () => {
        return (
            <ClbModal
                title={t('Deactivate?')}
                open={deactivateUserModalOpened}
                onClose={() => setDeactivateUserModalOpened(false)}
                description={t('DeactivateDescription')}
                type="danger"
                itemsFooter={[
                    {
                        id: 'confirm-button',
                        key: 'confirm-button',
                        label: t('Confirmar'),
                        disabled: false,
                        loading: false,
                        title: 'foo bar',
                        'data-testid': 'modal-confirm-button',
                        onClick: () => handleDeactivateUser(),
                        role: 'button',
                        size: 'md',
                        styleType: 'danger',
                        icon: 'delete',
                        iconPosition: 'left',
                    },
                    {
                        id: 'back-button',
                        key: 'back-button',
                        label: t('Voltar'),
                        disabled: false,
                        loading: false,
                        title: 'foo bar',
                        'data-testid': 'modal-back-button',
                        onClick: () => setDeactivateUserModalOpened(false),
                        role: 'button',
                        size: 'md',
                        styleType: 'secondary',
                        icon: 'blocked',
                        iconPosition: 'left',
                    },
                ]}
            />
        );
    };

    const tabs: TabsProps['items'] = [
        {
            key: '1',
            label: t('Usuarios'),
            children: renderUsersTab(),
        },
        {
            key: '2',
            label: t('Solicitacoes'),
            children: renderRequestsTab(),
        },
    ];

    return (
        <>
            {!isUserFormOpened && (
                <>
                    {!isUserEditFormOpened && (
                        <Subheader
                            title={t(UsersConstants.PAGE_TITLE)}
                            breadcrumbList={breadcrumbList}
                            data-testid="users-management-subheader"
                        />
                    )}
                    <div
                        className={`users-management-${
                            !isUserEditFormOpened ? 'content' : ''
                        }`}
                    >
                        <ToolbarTable
                            toolbarButtonList={toolbarButtonList}
                            toolbarIconsActions={toolbarIconsActionsList}
                            toolbarSearchCallback={toolbarSearchHandler}
                            setSearchTerm={setSearchTerm}
                            searchTerm={searchTerm}
                        />
                        <Tabs
                            defaultActiveKey={activeTab}
                            items={tabs}
                            onChange={tabSelector}
                        />
                    </div>
                </>
            )}

            {renderUserFormFullPageFlow()}
            {renderUserFormEdit()}
            {renderUserDeactivateModal()}
        </>
    );
}

export default UsersManagementForm;
